import { Typography, Paper, RadioGroup, FormControlLabel, Radio, Box } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { useFormContext, Controller } from 'react-hook-form';
import Input from '@/components/Common/Input';
import Select from '@/components/Common/Select';
import Autocomplete from '@/components/Common/Autocomplete';
import { useGetPublicCountries, useGetPublicStates } from '@/apis/agency/businessOnboarding/hooks';
import { Timezones } from '@/utils/timezones';
import BusinessHoursSection from '@/pages/Business/Setting/Locations/components/businessHours/BusinessHourSelection';
import { ScrollToTop } from '@/App';

export default function LocationDetailsForm() {
  const { control, watch, getValues, setValue } = useFormContext();

  const isPhysical = watch('location_details.physical_location_exists') !== false;
  const countryCode = watch('location_details.country');

  const { data: countries, isLoading: isCountriesLoading } = useGetPublicCountries();
  const { data: states, isLoading: isStatesLoading } = useGetPublicStates(countryCode || '');

  return (
    <Paper sx={{ border: '1px solid #EAEAEA', borderRadius: 2, p: { xs: 2, md: 3 } }}>
      <ScrollToTop state={2} />
      <Typography fontWeight={500} fontSize={20} mb={1}>
        Business Locations
      </Typography>

      <Typography variant='body2' color='text.secondary' mb={3}>
        Add your business location so the agent can handle calls, schedules, and time zones correctly.
      </Typography>

      <Grid container spacing={3}>
        <Grid size={12}>
          <Input
            name='location_details.count'
            label='Number Of Locations'
            control={control}
            required
            fullWidth
            placeholder='e.g 3'
          />
        </Grid>
      </Grid>

      <Paper variant='outlined' sx={{ mt: 4, p: { xs: 2, md: 3 }, borderRadius: 2 }}>
        <Typography fontWeight={500} mb={4}>
          First Location to Go Live
        </Typography>

        <Typography fontWeight={500}>Location</Typography>

        <Box mb={1.5}>
          <Controller
            name='location_details.physical_location_exists'
            control={control}
            defaultValue={true}
            render={({ field }) => (
              <RadioGroup
                row
                value={field.value === false ? 'remote' : 'physical'}
                onChange={(e) => field.onChange(e.target.value === 'physical')}
              >
                <FormControlLabel value='physical' control={<Radio />} label='Physical' />
                <FormControlLabel value='remote' control={<Radio />} label='Remote' />
              </RadioGroup>
            )}
          />
        </Box>

        <Grid container spacing={3} mt={1}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Input
              name='location_details.location_name'
              label='Location Friendly Name'
              placeholder='e.g Main Office'
              control={control}
              required
              fullWidth
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Autocomplete
              name='address.time_zone'
              label='Time Zone'
              control={control}
              options={Timezones}
              required
              fullWidth
            />
          </Grid>

          {isPhysical && (
            <>
              <Grid size={{ xs: 12, md: 6 }}>
                <Input
                  name='location_details.street'
                  label='Address Line 1'
                  placeholder='121, Business Ave'
                  control={control}
                  required
                  fullWidth
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Input
                  name='location_details.address2'
                  label='Address Line 2'
                  placeholder='Suite, Floor, etc'
                  control={control}
                  fullWidth
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Input
                  name='location_details.city'
                  label='City'
                  control={control}
                  placeholder='New York'
                  required
                  fullWidth
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Input
                  name='location_details.zip'
                  label='Postal / ZIP Code'
                  placeholder='10001'
                  control={control}
                  required
                  fullWidth
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Select
                  name='location_details.country'
                  label='Country'
                  control={control}
                  required
                  fullWidth
                  isLoading={isCountriesLoading}
                  options={countries || []}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Select
                  key={countryCode}
                  name='location_details.state'
                  label='State / Province / Region'
                  control={control}
                  required
                  fullWidth
                  isLoading={isStatesLoading}
                  options={states || []}
                />
              </Grid>
            </>
          )}
        </Grid>

        <Typography fontWeight={600} mt={4}>
          Business Hours
        </Typography>

        <Typography variant='body2' color='text.secondary' mb={2}>
          Set your business's operating hours and break times.
        </Typography>
        <Typography
          sx={{
            px: 2,
            py: 1.5,
            fontSize: 14,
            borderRadius: 2,
            bgcolor: '#FFFBEB',
            color: '#D97706'
          }}
        >
          NOTE - These hours tell callers when your business is open and are separate from your agent's availability or
          scheduling.
        </Typography>
        <BusinessHoursSection
          viewLabel={false}
          control={control}
          fieldName='business_hours'
          getValues={getValues}
          setValue={setValue}
        />
      </Paper>
    </Paper>
  );
}
